import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Divider,
  Grid,
  Group,
  Modal,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { IconAlertTriangle, IconCheck, IconEdit, IconEye, IconRefresh, IconX } from '@tabler/icons-react'
import { useState } from 'react'

interface Integration {
  id: string
  name: string
  type: 'promax' | 'sftp' | 'email' | 'sms'
  status: 'active' | 'inactive' | 'error'
  lastSync?: string
  config: any
}

export function IntegrationMonitor() {
  const integrations: Integration[] = [] // TODO: load from API when integration status endpoint exists
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null)
  const [showConfigModal, setShowConfigModal] = useState(false)

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'green'
      case 'inactive':
        return 'gray'
      case 'error':
        return 'red'
      default:
        return 'gray'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <IconCheck size={16} />
      case 'inactive':
        return <IconX size={16} />
      case 'error':
        return <IconAlertTriangle size={16} />
      default:
        return null
    }
  }

  return (
    <Stack gap="md">
      <Card withBorder>
        <Group justify="space-between" mb="md">
          <Title order={4}>Active Integrations</Title>
          <Button leftSection={<IconRefresh size={16} />} variant="outline">
            Refresh Status
          </Button>
        </Group>

        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Integration</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Last Sync</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {integrations.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text size="sm" c="dimmed" ta="center" py="xl">
                    No integrations configured. Integration status will appear here when available.
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              integrations.map(integration => (
                <Table.Tr key={integration.id}>
                  <Table.Td>
                    <Text fw={500}>{integration.name}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="outline" size="sm">
                      {integration.type.toUpperCase()}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={getStatusColor(integration.status)} leftSection={getStatusIcon(integration.status)}>
                      {integration.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {integration.lastSync ? new Date(integration.lastSync).toLocaleString() : 'Never'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        onClick={() => {
                          setSelectedIntegration(integration)
                          setShowConfigModal(true)
                        }}
                      >
                        <IconEye size={16} />
                      </ActionIcon>
                      <ActionIcon variant="subtle" size="sm">
                        <IconEdit size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Card>

      <Modal
        opened={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        title={`${selectedIntegration?.name} Configuration`}
        size="lg"
      >
        {selectedIntegration && (
          <Stack gap="md">
            <Alert icon={<IconAlertTriangle size={16} />} title="Configuration" color="blue">
              This is a read-only view of the integration configuration. Contact your system administrator to modify settings.
            </Alert>

            <Grid>
              <Grid.Col span={6}>
                <TextInput label="Integration Name" value={selectedIntegration.name} readOnly />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput label="Type" value={selectedIntegration.type} readOnly />
              </Grid.Col>
            </Grid>

            <Divider />

            <Title order={5}>Configuration Details</Title>
            <pre
              style={{
                background: '#f8f9fa',
                padding: '12px',
                borderRadius: '4px',
                fontSize: '12px',
                overflow: 'auto',
              }}
            >
              {JSON.stringify(selectedIntegration.config, null, 2)}
            </pre>
          </Stack>
        )}
      </Modal>
    </Stack>
  )
}
